import { useSelector } from "react-redux";
import { calculateAge } from "../../../utils/calculateAge";
import { useState } from "react";
import { setClient } from "../../../store/slices/clientsSlice";
import { setSelectedAppointment } from "../../../store/slices/appointmentsSlice";
import { useDispatch } from "react-redux";
import ClientModal from "../../../modals/ClientModal";
import { DocumentTextIcon, BanknotesIcon, CreditCardIcon } from "@heroicons/react/24/outline";

export const AppointmentClientInfo = ({ title = "Client", appointment }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const reduxSelectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  
  // Use passed appointment prop if provided, otherwise fall back to Redux state
  const selectedAppointment = appointment || reduxSelectedAppointment;
  
  // Enhanced null checking
  if (!selectedAppointment || !selectedAppointment.client) {
    return (
      <div className="border border-gray-200 rounded-sm px-4 py-4">
        <div className="mt-2">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Client Info
          </h3>
          <div className="text-sm text-gray-500 mt-2">
            Client information is not available for this appointment.
          </div>
        </div>
      </div>
    );
  }
  
  const client = selectedAppointment.client;
  const address = client?.address || {};

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    let date;
    // Parse date in local timezone to avoid off-by-one day errors
    if (dateString instanceof Date) {
      // Already a Date object from MongoDB
      date = dateString;
    } else if (typeof dateString === 'string') {
      // Check if it already has time component
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'T00:00:00');
      }
    } else {
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      weekday: "short",
      day: "numeric",
    });
  };

  const handleClientClick = () => {
    if (selectedAppointment?.client?._id) {
      setIsOpen(true);
      dispatch(setClient(selectedAppointment.client));
    }
  };

  return (
    <>
      <div
        className="border border-gray-200 rounded-sm px-4 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-150"
        onClick={handleClientClick}>
        <div className="mt-2">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Client Info
          </h3>
          <dl className={`grid gap-x-2 grid-cols-1 sm:grid-cols-3`}>
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Client Name
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {`${selectedAppointment.client?.firstName} ${selectedAppointment.client?.lastName}`}
              </dd>
            </div>
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Email
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {selectedAppointment.client?.email}
              </dd>
            </div>
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Phone Numbers
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {selectedAppointment.client?.phoneNumbers &&
                selectedAppointment.client?.phoneNumbers.length > 0
                  ? selectedAppointment.client?.phoneNumbers.map((phone) => (
                      <div key={phone._id}>
                        {phone.number} ({phone.type}) {phone.note}{" "}
                        {phone.type.toLowerCase() === "mobile" &&
                          phone.smsConsent && (
                            <svg
                              style={{ display: "inline-block" }}
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg">
                              <rect
                                x="4"
                                y="2"
                                width="14"
                                height="20"
                                rx="2"
                                stroke="#4CAF50"
                                strokeWidth="2"
                                fill="none"
                              />
                              <circle cx="11" cy="18" r="1" fill="#4CAF50" />
                              <path
                                d="M7 5C7 4.44772 7.44772 4 8 4H17C17.5523 4 18 4.44772 18 5V11C18 11.5523 17.5523 12 17 12H10L7 14V5Z"
                                fill="#4CAF50"
                              />
                              <line
                                x1="9"
                                y1="7"
                                x2="16"
                                y2="7"
                                stroke="white"
                                strokeWidth="1.5"
                              />
                              <line
                                x1="9"
                                y1="9"
                                x2="14"
                                y2="9"
                                stroke="white"
                                strokeWidth="1.5"
                              />
                              <circle
                                cx="18"
                                cy="18"
                                r="3"
                                stroke="#4CAF50"
                                strokeWidth="2"
                                fill="white"
                              />
                              <line
                                x1="18"
                                y1="16"
                                x2="18"
                                y2="18.5"
                                stroke="#4CAF50"
                                strokeWidth="2"
                              />
                              <line
                                x1="18"
                                y1="18"
                                x2="19.5"
                                y2="19"
                                stroke="#4CAF50"
                                strokeWidth="2"
                              />
                            </svg>
                          )}
                      </div>
                    ))
                  : "N/A"}
              </dd>
            </div>
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Date of Birth
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {formatDate(selectedAppointment.client?.dateOfBirth)}
              </dd>
            </div>
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Age
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {calculateAge(selectedAppointment.client?.dateOfBirth)}
              </dd>
            </div>

            {/* Contract Information */}
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                  Contract Status
                </div>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {client?.contract?.signed ? (
                  <div>
                    <span className="text-green-600">Signed</span>
                    {client.contract.signedDate && (
                      <div className="text-xs text-gray-500">
                        on {formatDate(client.contract.signedDate)}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-red-600">Not Signed</span>
                )}
              </dd>
            </div>

            {/* Payment Method */}
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <div className="flex items-center gap-2">
                  <BanknotesIcon className="h-4 w-4 text-gray-500" />
                  Default Payment Method
                </div>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {client?.defaultPaymentMethod?.name ? (
                  <div>
                    <span>{client.defaultPaymentMethod.name}</span>
                    {client.defaultPaymentMethod.lastFourDigits && (
                      <div className="text-xs text-gray-500">
                        Last 4: {client.defaultPaymentMethod.lastFourDigits}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">Not set</span>
                )}
              </dd>
            </div>

            {/* Credit Card on File */}
            <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                <div className="flex items-center gap-2">
                  <CreditCardIcon className="h-4 w-4 text-gray-500" />
                  Credit Card on File
                </div>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-1">
                {client?.creditCardOnFile ? (
                  <span className="text-green-600">Yes</span>
                ) : (
                  <span className="text-gray-500">No</span>
                )}
              </dd>
            </div>

            {/* Split Billing */}
            {(client?.splitBilling?.enabled !== null || client?.splitBilling?.instructions) && (
              <div className="border-t border-gray-100 px-2 py-2 sm:px-0 sm:col-span-3">
                <dt className="text-sm font-medium leading-6 text-gray-900 mb-2">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                    Split Billing
                  </div>
                </dt>
                <dd className="text-sm leading-6 text-gray-700 bg-blue-50 border border-blue-200 rounded-sm p-3 space-y-2">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      client?.splitBilling?.enabled === true
                        ? "bg-blue-100 text-blue-800"
                        : client?.splitBilling?.enabled === false
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                      Split Billing: {client?.splitBilling?.enabled === true
                        ? "Yes"
                        : client?.splitBilling?.enabled === false
                        ? "No"
                        : "Not Set"}
                    </span>
                  </div>
                  {client?.splitBilling?.instructions && (
                    <div className="pt-2 border-t border-blue-100">
                      <div className="font-medium text-gray-900 mb-1">Instructions:</div>
                      <div className="text-gray-700 whitespace-pre-wrap">
                        {client?.splitBilling?.instructions}
                      </div>
                    </div>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
      <ClientModal 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)}
        onAppointmentClick={(appointment) => {
          // Close the client modal first
          setIsOpen(false);
          // Small delay to allow modal to close, then update current AppointmentSummary
          setTimeout(() => {
            // Update Redux state to show the clicked appointment in current AppointmentSummary
            dispatch(setSelectedAppointment(appointment));
            // Scroll to top of the AppointmentSummary if possible
            if (window.scrollAppointmentSummaryToTop) {
              window.scrollAppointmentSummaryToTop();
            }
          }, 100);
        }}
      />
    </>
  );
};
